/**
 * ScamBlocker AI — Çağrı Geçmişi Sayfası (/calls)
 * Sayfalama, filtreleme, risk bazlı renklendirme.
 */

import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Phone, Search } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '@/shared/lib/api/client';
import { API } from '@/shared/lib/api/endpoints';
import styles from './CallsPage.module.css';

interface CallRecord {
  id: string;
  caller_number?: string;
  risk_score?: number;
  risk_level?: string;
  duration_seconds?: number;
  scenario_match?: string;
  was_monitored: boolean;
  started_at: string;
  ended_at?: string;
}

interface CallsResponse {
  calls: CallRecord[];
  total: number;
  page: number;
  per_page: number;
}

const PAGE_SIZE = 25;

const RISK_COLOR: Record<string, string> = {
  SAFE: 'var(--color-success)',
  CAUTION: 'var(--color-warning)',
  DANGER: 'var(--color-danger)',
  CRITICAL: 'var(--color-critical)',
};

function formatDuration(secs?: number): string {
  if (!secs) return '—';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function CallsPage() {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');

  const { data, isLoading, isError } = useQuery<CallsResponse>({
    queryKey: ['calls', page, search],
    queryFn: async () => {
      const { data } = await apiClient.get(API.org.calls, {
        params: { page, per_page: PAGE_SIZE, q: search || undefined },
      });
      return data;
    },
  });

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 1;

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <Phone size={22} className={styles.titleIcon} />
          <h1 className={styles.title}>{t('calls.title')}</h1>
          {data && <span className={styles.countPill}>{data.total} kayıt</span>}
        </div>

        {/* Arama */}
        <div className={styles.searchBox}>
          <Search size={15} className={styles.searchIcon} />
          <input
            className={styles.searchInput}
            type="text"
            placeholder={t('calls.searchPlaceholder')}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>
      </div>

      {/* Tablo */}
      <div className={styles.tableWrapper}>
        {isLoading && (
          <div className={styles.center}>
            <span className={styles.spinner} />
          </div>
        )}
        {isError && (
          <div className={styles.center} style={{ color: 'var(--color-danger)' }}>
            {t('calls.loadError')}
          </div>
        )}
        {!isLoading && !isError && data && (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('calls.colDateTime')}</th>
                <th>{t('calls.colNumber')}</th>
                <th>{t('calls.colDuration')}</th>
                <th>{t('calls.colRisk')}</th>
                <th>{t('calls.colScenario')}</th>
                <th>{t('calls.colMonitored')}</th>
              </tr>
            </thead>
            <tbody>
              {data.calls.map((call) => {
                const riskColor = RISK_COLOR[call.risk_level ?? ''] ?? 'var(--color-muted)';
                return (
                  <tr key={call.id} className={styles.row}>
                    <td className={styles.mono}>
                      {new Date(call.started_at).toLocaleString('tr-TR')}
                    </td>
                    <td className={styles.mono}>{call.caller_number ?? '—'}</td>
                    <td className={styles.mono}>{formatDuration(call.duration_seconds)}</td>
                    <td>
                      {call.risk_level ? (
                        <span
                          className={styles.riskBadge}
                          style={{
                            color: riskColor,
                            borderColor: riskColor,
                            background: `${riskColor}18`,
                          }}
                        >
                          {call.risk_score ?? '—'} · {call.risk_level}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className={styles.scenario}>{call.scenario_match ?? '—'}</td>
                    <td>{call.was_monitored ? '✓' : '—'}</td>
                  </tr>
                );
              })}
              {data.calls.length === 0 && (
                <tr>
                  <td colSpan={6} className={styles.empty}>
                    Kayıt bulunamadı.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Sayfalama */}
      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            type="button"
            className={styles.pageBtn}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
          >
            <ChevronLeft size={16} />
          </button>
          <span className={styles.pageInfo}>
            {page} / {totalPages}
          </span>
          <button
            type="button"
            className={styles.pageBtn}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
}
